import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import useIncidenciasPanel from '../../hooks/useIncidenciasPanel';
import useCategorias from '../../hooks/useCategorias';
import DataTable from '../../components/Panel/DataTable';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const FILTROS_ESTADO = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'recepcionado', etiqueta: 'Recepcionado' },
  { valor: 'en_progreso', etiqueta: 'En proceso' },
  { valor: 'culminado', etiqueta: 'Culminado' },
  { valor: 'anulado', etiqueta: 'Anulado' },
];

const ESTADOS_MASIVO = [
  { valor: 'recepcionado', etiqueta: 'Recepcionado' },
  { valor: 'en_progreso', etiqueta: 'En progreso' },
  { valor: 'culminado', etiqueta: 'Culminado' },
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'anulado', etiqueta: 'Anulado' },
];

const PAGE_SIZE_TODOS = 1000; // tope real que acepta el backend para "Todos"

// "2F 1V 1A" — recuento compacto de evidencia por tipo.
const INICIAL_TIPO = { Imagen: 'F', Video: 'V', Audio: 'A', Documento: 'D' };
const recuentoEvidencia = (evidencia) => {
  if (!evidencia?.length) return '—';
  const conteo = {};
  evidencia.forEach((ev) => {
    const letra = INICIAL_TIPO[ev.arc_chTipoArchivo] || '?';
    conteo[letra] = (conteo[letra] || 0) + 1;
  });
  return Object.entries(conteo).map(([letra, n]) => `${n}${letra}`).join(' ');
};

// "2026-09" -> { desde: '2026-09-01', hasta: '2026-09-30' }
const rangoDelMes = (mesInput) => {
  const [anio, mes] = mesInput.split('-').map(Number);
  const desde = `${mesInput}-01`;
  const ultimoDia = new Date(anio, mes, 0).getDate();
  const hasta = `${mesInput}-${String(ultimoDia).padStart(2, '0')}`;
  return { desde, hasta };
};

const mesActual = () => new Date().toISOString().slice(0, 7);

/**
 * Listado de incidencias del panel. Filtro por mes + categoría + píldoras de
 * estado, tabla con paginación, selección múltiple para cambiar el estado en
 * bloque y atajos Anular / Activar.
 */
const Tickets = () => {
  const { loading, error, listarIncidencias, cambiarStatusMasivo } = useIncidenciasPanel();
  const { categorias } = useCategorias();
  const navigate = useNavigate();

  const [incidencias, setIncidencias] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filtroTicket, setFiltroTicket] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [mes, setMes] = useState(mesActual());
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [estadoMasivo, setEstadoMasivo] = useState('recepcionado');
  const [guardandoMasivo, setGuardandoMasivo] = useState(false);

  const pageSizeEfectivo = pageSize === 'todos' ? PAGE_SIZE_TODOS : pageSize;

  const cargar = useCallback(async () => {
    const { desde, hasta } = rangoDelMes(mes);
    const resultado = await listarIncidencias({
      ticket: filtroTicket,
      status: filtroStatus,
      categoria: filtroCategoria,
      fecha_desde: desde,
      fecha_hasta: hasta,
      page,
      page_size: pageSizeEfectivo,
    });
    if (resultado.success) {
      setIncidencias(resultado.resultados);
      setTotal(resultado.total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTicket, filtroStatus, filtroCategoria, mes, page, pageSizeEfectivo]);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleSel = (id) => setSeleccionados((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const limpiarSel = () => setSeleccionados(new Set());
  const todosPagina = incidencias.length > 0 && incidencias.every((i) => seleccionados.has(i.inc_inId));
  const toggleTodosPagina = () => setSeleccionados((s) => {
    if (incidencias.length && incidencias.every((i) => s.has(i.inc_inId))) return new Set();
    return new Set(incidencias.map((i) => i.inc_inId));
  });

  const aplicarMasivo = async (status, observaciones = '') => {
    if (seleccionados.size === 0) return;
    setGuardandoMasivo(true);
    const r = await cambiarStatusMasivo([...seleccionados], status, observaciones);
    setGuardandoMasivo(false);
    if (r.success) {
      limpiarSel();
      cargar();
      Swal.fire({ icon: 'success', title: 'Listo', text: r.resultado?.mensaje || 'Estado actualizado.', timer: 1800, showConfirmButton: false });
    } else {
      Swal.fire({ icon: 'error', title: 'Error', text: r.error });
    }
  };

  const columnas = useMemo(() => [
    {
      id: 'sel',
      disableSortBy: true,
      Header: () => (
        <input
          type="checkbox"
          checked={todosPagina}
          onClick={(e) => { e.stopPropagation(); toggleTodosPagina(); }}
          onChange={() => {}}
          aria-label="Seleccionar página"
        />
      ),
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={seleccionados.has(row.original.inc_inId)}
          onClick={(e) => { e.stopPropagation(); toggleSel(row.original.inc_inId); }}
          onChange={() => {}}
          aria-label="Seleccionar"
        />
      ),
    },
    {
      Header: 'Código',
      accessor: 'inc_chTiket',
      Cell: ({ value }) => <span className="panel-datatable-codigo">{value}</span>,
    },
    {
      Header: 'Incidencia',
      accessor: (fila) => fila.fk_categoria?.cat_chNombre || 'Sin categoría',
      id: 'incidencia',
      Cell: ({ row, value }) => (
        <div>
          <div style={{ fontWeight: 700 }}>{value}</div>
          <div className="panel-datatable-direccion">{row.original.inc_chDireccion || 'Sin dirección'}</div>
        </div>
      ),
    },
    { Header: 'Ocurrió', accessor: 'fecha_creacion_formateada' },
    {
      Header: 'Evidencia',
      accessor: (fila) => fila.evidencia?.length || 0,
      id: 'evidencia',
      Cell: ({ row }) => <span className="panel-datatable-evidencia">{recuentoEvidencia(row.original.evidencia)}</span>,
    },
    {
      Header: 'Severidad',
      accessor: 'inc_chSeveridad',
      Cell: ({ value, row }) => (
        <span className={`pildora-severidad pildora-severidad-${value || 'sin-asignar'}`}>
          {row.original.inc_chSeveridad_display || 'Sin asignar'}
        </span>
      ),
    },
    {
      Header: 'Estado',
      accessor: 'inc_chStatus',
      Cell: ({ value, row }) => (
        <span className={`pildora-estado pildora-estado-${value}`}>{row.original.inc_chStatus_display}</span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [seleccionados, todosPagina, incidencias]);

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <h2 style={{ margin: 0 }}>Incidencias</h2>
      </div>

      <div className="panel-gu-filtros panel-tickets-filtros">
        <div className="panel-buscador">
          <i className="fas fa-magnifying-glass"></i>
          <input
            placeholder="Buscar por código…"
            value={filtroTicket}
            onChange={(e) => { setPage(1); setFiltroTicket(e.target.value); }}
          />
        </div>

        <select
          className="panel-input"
          style={{ maxWidth: 220 }}
          value={filtroCategoria}
          onChange={(e) => { setPage(1); setFiltroCategoria(e.target.value); }}
        >
          <option value="">Todas las categorías</option>
          {categorias.map((c) => (
            <option key={c.cat_inId} value={c.cat_inId}>{c.cat_chNombre}</option>
          ))}
        </select>

        <div className="panel-selector-mes">
          <i className="fas fa-calendar-days"></i>
          <input
            type="month"
            value={mes}
            onChange={(e) => { setPage(1); setMes(e.target.value); }}
          />
        </div>

        <div className="panel-filtro-pildoras">
          {FILTROS_ESTADO.map((f) => (
            <button
              key={f.valor}
              type="button"
              className={`panel-filtro-pildora ${filtroStatus === f.valor ? 'panel-filtro-pildora-activa' : ''}`}
              onClick={() => { setPage(1); setFiltroStatus(f.valor); }}
            >
              {f.etiqueta}
            </button>
          ))}
        </div>
      </div>

      {seleccionados.size > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 12, background: '#eef4fb', borderRadius: 12 }}>
          <strong>{seleccionados.size} seleccionada(s)</strong>
          <select className="panel-input" style={{ maxWidth: 190 }} value={estadoMasivo} onChange={(e) => setEstadoMasivo(e.target.value)}>
            {ESTADOS_MASIVO.map((e) => <option key={e.valor} value={e.valor}>{e.etiqueta}</option>)}
          </select>
          <button type="button" className="panel-boton-primario" disabled={guardandoMasivo} onClick={() => aplicarMasivo(estadoMasivo)}>
            {guardandoMasivo ? 'Aplicando…' : 'Aplicar estado'}
          </button>
          <button type="button" className="panel-boton-secundario" disabled={guardandoMasivo} onClick={() => aplicarMasivo('anulado')}>Anular</button>
          <button type="button" className="panel-boton-secundario" disabled={guardandoMasivo} onClick={() => aplicarMasivo('pendiente')}>Activar</button>
          <button type="button" className="panel-boton-secundario" onClick={limpiarSel}>Limpiar</button>
        </div>
      )}

      {error && <p className="panel-gu-mensaje">{error}</p>}

      <DataTable
        columns={columnas}
        data={incidencias}
        onRowClick={(fila) => navigate(`/panel/tickets/${fila.inc_inId}`)}
        filaVacia={loading ? 'Cargando…' : 'No hay incidencias con estos filtros.'}
        paginacion={{
          page, pageSize, pageSizeEfectivo, total,
          onPageChange: setPage,
          onPageSizeChange: (nuevo) => { setPage(1); setPageSize(nuevo); },
        }}
      />
    </PanelShell>
  );
};

export default Tickets;
